import { GroupSecurity } from '@quromedical/fhir-common';
import type { OrganizationGeneral, PersonContact } from '@quromedical/models';
import { OrganizationGeneralForm } from 'core/organization';
import {
  Alert,
  Section,
  Skeleton,
  SkeletonProvider,
  Snackbar,
} from 'design-system';
import { useLocalSearchParams } from 'expo-router';
import { onMapsPress } from 'helpers/platform-links';
import { useUserSession } from 'hooks';
import {
  getPlaceAddressPartialFetcher,
  googleSearchAddressFetcher,
  schemeCodeFetcher,
} from 'integration';
import { OrganizationApi } from 'integration/aggregate';
import type { OrganizationStackScreenProps } from 'navigation';
import type React from 'react';
import { useCallback } from 'react';
import { ScrollView } from 'react-native-gesture-handler';
import { PersonContactForm } from 'screens/person';
import { strings } from 'strings';
import { trpc } from 'trpc';

const api = new OrganizationApi();

type RouteParams = {
  id: string;
};

export const OrganizationScreen: React.FC<
  OrganizationStackScreenProps<'View'>
> = () => {
  const session = useUserSession();

  const canEdit = session.hasAny([GroupSecurity.OrganizationUpdate]);

  const { id } = useLocalSearchParams<RouteParams>();

  const {
    data,
    isRefetching: isValidating,
    error,
    refetch: revalidate,
  } = trpc.organization.getById.useQuery({ id });

  const onGeneralSubmit = useCallback(
    async (formData: OrganizationGeneral) => {
      await api.updateOrganizationGeneral({
        ...formData,
        id,
      });
    },
    [id],
  );

  const onContactSubmit = useCallback(
    async (results: PersonContact) => {
      await api.updateOrganizationContact({
        id,
        contact: results,
      });
    },
    [id],
  );

  const onMapsPressed = useCallback(() => {
    onMapsPress(data?.contact);
  }, [data?.contact]);

  if (error) {
    return (
      <Snackbar onClose={revalidate} isOpen={!!error}>
        <Alert
          backgroundColor="status-critical"
          textColor="white"
          onAction={revalidate}
          actionIcon="close"
          title={strings.ErrorCardTitle}
          body={strings.GenericErrorMessage}
        />
      </Snackbar>
    );
  }

  const loading = isValidating || !data;

  const isButtonDisabled = !data?.contact?.addressCoordinates;

  return (
    <ScrollView>
      <SkeletonProvider loading={loading}>
        <Section unsetZIndex>
          <Skeleton>
            <OrganizationGeneralForm
              schemeFetcher={schemeCodeFetcher}
              revalidate={revalidate}
              canEdit={canEdit}
              data={data?.general}
              onSubmit={onGeneralSubmit}
            />
          </Skeleton>
        </Section>
        <Section>
          <Skeleton>
            <PersonContactForm
              canEdit={canEdit}
              data={data?.contact}
              onSubmit={onContactSubmit}
              revalidate={revalidate}
              hasEmergencyAccess={false}
              addressFetcher={googleSearchAddressFetcher}
              getAddressPartial={getPlaceAddressPartialFetcher}
              onMapsPress={onMapsPressed}
              isButtonDisabled={isButtonDisabled}
            />
          </Skeleton>
        </Section>
      </SkeletonProvider>
    </ScrollView>
  );
};
